"use client";

const sizes = {
  sm: "px-3 py-1.5 text-xs rounded-md",
  md: "px-4 py-2 rounded-lg",
  lg: "px-5 py-2.5 rounded-xl !lg-w-1/3",
};

const variants = {
  primary: "tycho-btn-primary",
  secondary: "tycho-btn-secondary",
  ghost: `
    text-blue-600 hover:bg-blue-100 
    dark:text-blue-400 dark:hover:bg-blue-900/30
  `,
  white: `
    bg-white text-blue-600 border 
    hover:bg-gray-100
    dark:bg-gray-100 dark:text-blue-700
  `,
  danger: `
    bg-red-500 text-white 
    hover:bg-red-600 
    focus:ring-red-400
    dark:bg-red-600 dark:hover:bg-red-500
  `,
};

export default function TychoButton({
  title,
  variant = "primary",
  size = "md",
  className = "",
  icon,
  href,
  disabled = false,
  target = "_self",
  titleSize = "16px",
  titleWeight = 400,
  ...props
}) {
  const base =
    "inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer";

  const classes = [base, sizes[size], variants[variant], className].join(" ");

  const content = (
    <>
      <span className="btn-sweep" aria-hidden="true" />
      <span className={`relative z-10 flex items-center gap-2`} style={{ fontSize: titleSize, fontWeight: titleWeight }}>
        {title}
        {icon && <span className="flex-shrink-0">{icon}</span>}
      </span>
    </>
  );

  if (href) {
    return (
      <>
        <ButtonStyles />
        <a href={href} className={classes} target={target}>
          {content}
        </a>
      </>
    );
  }

  return (
    <>
      <ButtonStyles />
      <button className={classes} disabled={disabled} {...props}>
        {content}
      </button>
    </>
  );
}

function ButtonStyles() {
  return (
    <style>{`
      /* ─────────────────── SHARED GRADIENT ─────────────────── */

      :root {
        --grad: linear-gradient(110.23deg, #7949CD 6.47%, #6B58F5 44.08%, #0EA7F0 83.15%);
      }

      .tycho-btn-primary,
      .tycho-btn-secondary {
        font-family: 'Inter', sans-serif;
        font-size: clamp(14px, 2vw, 24px);
      }

      /* ─────────────────── PRIMARY ─────────────────── */

      .tycho-btn-primary {
        position: relative;
        overflow: hidden;
        background: var(--grad);
        color: #ffffff;
        border: none;
        box-shadow: 0 0 20px rgba(107, 88, 245, 0.30);
        z-index: 0;
        transition:
          opacity 0.25s ease,
          transform 0.25s ease,
          box-shadow 0.25s ease;
      }

      /* reversed sweep on hover */
      .tycho-btn-primary .btn-sweep {
        position: absolute;
        inset: 0;
        background: linear-gradient(110.23deg, #0EA7F0 6.47%, #6B58F5 44.08%, #7949CD 83.15%);
        transform: translateX(-101%);
        transition: transform 0.46s cubic-bezier(0.4, 0, 0.2, 1) 0.04s;
        z-index: 1;
        border-radius: inherit;
      }

      .tycho-btn-primary:hover .btn-sweep,
      .tycho-btn-primary:focus-visible .btn-sweep {
        transform: translateX(0%);
      }

      .tycho-btn-primary:hover,
      .tycho-btn-primary:focus-visible {
        opacity: 0.95;
        transform: translateY(-1px);
        box-shadow: 0 0 28px rgba(107, 88, 245, 0.45);
        color: #ffffff;
      }

      .tycho-btn-primary > *:not(.btn-sweep) {
        position: relative;
        z-index: 2;
      }

      /* ─────────────────── SECONDARY ─────────────────── */

      .tycho-btn-secondary {
        position: relative;
        overflow: hidden;
        background: transparent;

        /* gradient border via ::before mask */
        border: none;
        box-shadow: none;

        z-index: 0;
        transition:
          color 0.42s ease,
          transform 0.25s ease;
      }

      /* gradient border */
      .tycho-btn-secondary::before {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: inherit;
        padding: 1.5px;
        background: var(--grad);
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        pointer-events: none;
        z-index: 0;
        transition: opacity 0.42s ease;
      }

      /* sweep fill — slides in on hover */
      .tycho-btn-secondary .btn-sweep {
        position: absolute;
        inset: 0;
        background: var(--grad);
        transform: translateX(-101%);
        transition: transform 0.46s cubic-bezier(0.4, 0, 0.2, 1) 0.04s;
        z-index: -1;
        border-radius: inherit;
      }

      .tycho-btn-secondary:hover .btn-sweep,
      .tycho-btn-secondary:focus-visible .btn-sweep {
        transform: translateX(0%);
      }

      /* gradient text at rest */
      .tycho-btn-secondary > *:not(.btn-sweep) {
        position: relative;
        z-index: 1;
        background: var(--grad);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        transition: background 0s, -webkit-text-fill-color 0.28s ease 0.12s;
      }

      /* white text once sweep covers the button */
      .tycho-btn-secondary:hover > *:not(.btn-sweep),
      .tycho-btn-secondary:focus-visible > *:not(.btn-sweep) {
        -webkit-text-fill-color: #fff;
        background: none;
        -webkit-background-clip: unset;
        background-clip: unset;
      }

      .tycho-btn-secondary:not(:hover):not(:focus-visible) > *:not(.btn-sweep) {
        transition: -webkit-text-fill-color 0.22s ease;
      }

      .tycho-btn-secondary:hover,
      .tycho-btn-secondary:focus-visible {
        color: #fff;
      }

      .tycho-btn-secondary:hover {
        transform: translateY(-1px);
      }

      /* ─────────────────── DARK MODE ─────────────────── */

      [data-theme="dark"] .tycho-btn-primary {
        box-shadow: 0 0 24px rgba(107, 88, 245, 0.45);
      }

      [data-theme="dark"] .tycho-btn-primary:hover {
        box-shadow: 0 0 32px rgba(107, 88, 245, 0.60);
      }
    `}</style>
  );
}
